import type { vtkObject } from '@kitware/vtk.js/interfaces';
import type { VtkProxy } from '@kitware/vtk.js/macros';
import type { vtkAbstractRepresentationProxy } from '@kitware/vtk.js/Proxy/Core/AbstractRepresentationProxy';
import type vtkProxyManager from '@kitware/vtk.js/Proxy/Core/ProxyManager';
import type { vtkSourceProxy } from '@kitware/vtk.js/Proxy/Core/SourceProxy';
import type { vtkViewProxy } from '@kitware/vtk.js/Proxy/Core/ViewProxy';

// mapped in proxy.js
export enum ViewProxyType {
  Volume = 'View3D',
  Slice = 'View2D',
}

/**
 * Wrapper around the vtkProxyManager, since we don't need some of
 * its complexities.
 */
export default class ProxyWrapper {
  private viewProxies: Map<string, vtkViewProxy>;
  private dataProxies: Map<string, vtkSourceProxy<vtkObject>>;
  private proxyManager: vtkProxyManager;

  constructor(proxyManager: vtkProxyManager) {
    this.viewProxies = new Map();
    this.dataProxies = new Map();
    this.proxyManager = proxyManager;
  }

  delete() {
    const deleteProxy = (proxy: VtkProxy) =>
      this.proxyManager.deleteProxy(proxy);

    this.viewProxies.forEach(deleteProxy);
    this.dataProxies.forEach(deleteProxy);
  }

  createView(id: string, type: ViewProxyType) {
    if (this.viewProxies.has(id)) {
      throw new Error('Cannot create a view with the same ID');
    }

    const proxy = this.proxyManager.createProxy<vtkViewProxy>('Views', type, {
      name: type,
    });

    this.viewProxies.set(id, proxy);
    // // Ensure representations for all existing data in this new view
    // // ToDo review
    // this.dataProxies.forEach((dataProxy) => {
    //   this.proxyManager.getRepresentation(dataProxy, proxy);
    // });
    return proxy;
  }

  getView<T extends vtkViewProxy>(id: string) {
    return <T | null> this.viewProxies.get(id);
  }

  removeView(id: string) {
    const proxy = this.viewProxies.get(id);
    if (proxy) {
      this.proxyManager.deleteProxy(proxy);
      this.viewProxies.delete(id);
    }
  }

  addData<T extends vtkObject>(id: string, data: T) {
    if (this.dataProxies.has(id)) {
      return;
    }
    const proxy = this.proxyManager.createProxy<vtkSourceProxy<T>>(
      'Sources',
      'TrivialProducer',
    );
    proxy.setInputData(data);
    this.dataProxies.set(id, proxy);
    // // Ensure representations for all existing views
    // // ToDo review
    // this.viewProxies.forEach((viewProxy) => {
    //   this.proxyManager.getRepresentation(proxy, viewProxy);
    // });
  }

  getData<T extends vtkObject>(id: string) {
    return <vtkSourceProxy<T> | null>(this.dataProxies.get(id) ?? null);
  }

  updateData<T extends vtkObject>(id: string, data: T) {
    const proxy = this.dataProxies.get(id);
    if (!proxy) {
      return;
    }
    proxy.setInputData(data);
  }

  deleteData(id: string) {
    const proxy = this.dataProxies.get(id);
    if (!proxy) return;

    // Clean up representations before deleting the proxy
    this.viewProxies.forEach((viewProxy) => {
      try {
        const rep = this.proxyManager.getRepresentation(proxy, viewProxy);
        if (rep) viewProxy.removeRepresentation(rep);
      }
      catch {
        /* no representation for this view */
      }
    });

    try {
      this.proxyManager.deleteProxy(proxy);
    }
    catch (error) {
      console.error('Error during deleteProxy:', error);
    }

    this.dataProxies.delete(id);
  }

  getDataRepresentationForView<T extends vtkAbstractRepresentationProxy>(
    dataID: string,
    viewID: string,
  ): T | null {
    const dataProxy = this.dataProxies.get(dataID);
    const viewProxy = this.viewProxies.get(viewID);
    if (!dataProxy || !viewProxy) {
      return null;
    }

    return this.proxyManager.getRepresentation(dataProxy, viewProxy);
  }
}
